import { useEffect, useState } from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import {
  DeviationReasonSelect,
  type DeviationReasonSelectProps,
} from "./DeviationReasonSelect";

export type LazyDeviationReasonSelectProps = DeviationReasonSelectProps;

function resolveDisplayLabel(
  selectedLabel: string | null | undefined,
  value: string,
  placeholder: string,
): string {
  const label = selectedLabel?.trim();
  if (label) {
    return label;
  }
  const trimmedValue = value.trim();
  if (trimmedValue) {
    return trimmedValue;
  }
  return placeholder;
}

/**
 * Monta `DeviationReasonSelect` solo al hacer clic, para tablas con miles de celdas.
 */
export function LazyDeviationReasonSelect(props: LazyDeviationReasonSelectProps) {
  const {
    value,
    selectedLabel,
    onChange,
    isDisabled = false,
    placeholder = "Motivo…",
    size = "sm",
  } = props;

  const [editing, setEditing] = useState(false);
  const [resolvedLabel, setResolvedLabel] = useState(
    resolveDisplayLabel(selectedLabel, value, placeholder),
  );

  useEffect(() => {
    setResolvedLabel(resolveDisplayLabel(selectedLabel, value, placeholder));
    setEditing(false);
  }, [selectedLabel, value, placeholder]);

  const borderColor = useColorModeValue("gray.300", "whiteAlpha.300");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const mutedColor = useColorModeValue("secondaryGray.600", "gray.400");
  const hoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");

  const hasValue = Boolean(
    selectedLabel?.trim() || value.trim(),
  );
  const displayText = resolvedLabel;
  const minHeight = size === "sm" ? "28px" : "36px";
  const fontSize = size === "sm" ? "10px" : "sm";

  if (!editing) {
    const readOnlyEmpty = isDisabled && !hasValue;

    return (
      <Box
        role={isDisabled ? undefined : "button"}
        tabIndex={isDisabled ? undefined : 0}
        aria-label={isDisabled ? undefined : "Editar motivo de desviación"}
        onClick={() => {
          if (!isDisabled) {
            setEditing(true);
          }
        }}
        onKeyDown={(event) => {
          if (isDisabled) {
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setEditing(true);
          }
        }}
        minH={minHeight}
        px={2}
        py={1}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        cursor={isDisabled ? "default" : "pointer"}
        opacity={isDisabled ? 0.85 : 1}
        w="100%"
        minW={0}
        display="flex"
        alignItems="center"
        _hover={isDisabled ? undefined : { bg: hoverBg }}
      >
        <Text
          fontSize={fontSize}
          color={readOnlyEmpty ? mutedColor : textColor}
          noOfLines={2}
          lineHeight="short"
          w="100%"
        >
          {readOnlyEmpty ? "—" : displayText}
        </Text>
      </Box>
    );
  }

  return (
    <DeviationReasonSelect
      {...props}
      defaultMenuIsOpen
      onDismiss={() => setEditing(false)}
      onChange={(nextValue, label) => {
        const nextLabel = label?.trim() || nextValue.trim() || placeholder;
        setResolvedLabel(nextLabel);
        onChange(nextValue, label);
        setEditing(false);
      }}
    />
  );
}
